import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  LinearProgress,
  Autocomplete,
  Chip,
  CircularProgress,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useS3Upload } from "../../hooks/useS3Upload";
import { useCompanies } from "../../hooks/useCompanies";
import { usePositions } from "../../hooks/usePositions";
import { useUsers } from "../../hooks/useUsers";

const KINDS = [
  { value: "pdf", label: "PDF" },
  { value: "video", label: "Vídeo" },
  { value: "image", label: "Imagem" },
  { value: "link", label: "Link / outro" },
];

function newId(prefix) {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function cleanIdList(arr) {
  if (!Array.isArray(arr)) return [];
  return [...new Set(arr.map((n) => Number(n)).filter((n) => Number.isInteger(n) && n > 0))];
}

export function emptyTrainingKb() {
  return {
    format: "training_kb.v1",
    intro: "",
    modules: [],
    visibility: { ids_projetos: [], ids_cargos: [], ids_usuarios: [] },
  };
}

export function sanitizeTrainingKb(src) {
  const v = src && typeof src === "object" ? src : {};
  const modules = Array.isArray(v.modules) ? v.modules : [];
  const cleanItems = (items) =>
    (Array.isArray(items) ? items : [])
      .map((it) => ({
        id: String(it?.id || "").trim() || newId("item"),
        kind: ["pdf", "video", "image", "link"].includes(String(it?.kind || "").toLowerCase())
          ? String(it.kind).toLowerCase()
          : "pdf",
        title: String(it?.title || "").trim(),
        url: String(it?.url || "").trim(),
        notes: String(it?.notes || "").trim(),
        order: Number.isFinite(Number(it?.order)) ? Math.trunc(Number(it.order)) : 0,
      }))
      .filter((it) => it.title || it.url || it.notes);

  const cleanModules = modules
    .map((m) => ({
      id: String(m?.id || "").trim() || newId("mod"),
      title: String(m?.title || "").trim(),
      order: Number.isFinite(Number(m?.order)) ? Math.trunc(Number(m.order)) : null,
      items: cleanItems(m?.items),
    }))
    .filter((m) => m.title || (m.items && m.items.length));

  const orderedModules = cleanModules.map((m, mi) => ({
    ...m,
    order: m.order != null ? m.order : mi,
    items: (m.items || []).map((it, ii) => ({ ...it, order: ii })),
  }));

  return {
    format: "training_kb.v1",
    intro: String(v.intro || "").trim(),
    modules: orderedModules,
  };
}

export default function TrainingKbEditor({ value, onChange }) {
  const { uploadOne } = useS3Upload();
  const [uploadKey, setUploadKey] = useState(null);
  const [uploading, setUploading] = useState(false);
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const { data: companiesRes, isLoading: loadingCompanies } = useCompanies(1, 500, "");
  const companies = companiesRes?.companies || [];
  const { data: posRes, isLoading: loadingPositions } = usePositions(1, 200, "");
  const positions = posRes?.positions || [];
  const { data: usersRes, isLoading: loadingUsers } = useUsers(1, 300, "", "humanos");
  const users = usersRes?.users || [];

  const data = value && value.format === "training_kb.v1" ? value : emptyTrainingKb();
  const vis = data.visibility || { ids_projetos: [], ids_cargos: [], ids_usuarios: [] };

  const patchVis = (patch) =>
    onChange({
      ...data,
      visibility: { ...vis, ...patch },
    });

  const setIntro = (intro) => onChange({ ...data, intro });

  const addModule = () => {
    onChange({
      ...data,
      modules: [
        ...data.modules,
        { id: newId("mod"), title: "", order: data.modules.length, items: [] },
      ],
    });
  };

  const patchModule = (idx, patch) => {
    const modules = [...data.modules];
    modules[idx] = { ...modules[idx], ...patch };
    onChange({ ...data, modules });
  };

  const removeModule = (idx) => {
    onChange({ ...data, modules: data.modules.filter((_, i) => i !== idx) });
  };

  const addItem = (modIdx) => {
    const mod = data.modules[modIdx];
    const items = [...(mod.items || []), { id: newId("item"), kind: "pdf", title: "", url: "", notes: "", order: (mod.items || []).length }];
    patchModule(modIdx, { items });
  };

  const patchItem = (modIdx, itemIdx, patch) => {
    const mod = data.modules[modIdx];
    const items = [...(mod.items || [])];
    items[itemIdx] = { ...items[itemIdx], ...patch };
    patchModule(modIdx, { items });
  };

  const removeItem = (modIdx, itemIdx) => {
    const mod = data.modules[modIdx];
    patchModule(modIdx, {
      items: (mod.items || []).filter((_, i) => i !== itemIdx),
    });
  };

  const handleFilePick = useCallback(async (modIdx, itemIdx, file) => {
    if (!file) return;
    setUploading(true);
    setUploadKey(`${modIdx}-${itemIdx}`);
    try {
      const { url } = await uploadOne(file, { prefix: "training" });
      const d = valueRef.current && valueRef.current.format === "training_kb.v1" ? valueRef.current : emptyTrainingKb();
      const modules = [...d.modules];
      const mod = modules[modIdx];
      if (!mod) return;
      const items = [...(mod.items || [])];
      if (!items[itemIdx]) return;
      items[itemIdx] = { ...items[itemIdx], url };
      modules[modIdx] = { ...mod, items };
      onChange({ ...d, modules });
    } catch (e) {
      console.error(e);
    } finally {
      setUploading(false);
      setUploadKey(null);
    }
  }, [onChange, uploadOne]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="subtitle2" color="text.secondary">
        Estruture módulos e materiais (PDF, vídeo, imagem). Para a IA entender vídeos e imagens, preencha o campo de notas
        (resumo ou transcrição).
      </Typography>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Quem pode ver este treinamento
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Deixe os três campos vazios para liberar a todos do workspace. Se preencher, o usuário precisa atender a todas as
          listas usadas (empresa no contexto atual, cargo e/ou usuário). Dentro de cada lista, basta um item coincidir.
          Usuários master veem todos os treinamentos.
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Autocomplete
            multiple
            loading={loadingCompanies}
            options={companies}
            getOptionLabel={(o) => (o && o.nome ? String(o.nome) : o?.id != null ? `Projeto #${o.id}` : "")}
            isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
            value={companies.filter((c) => vis.ids_projetos.includes(Number(c.id)))}
            onChange={(_, v) => patchVis({ ids_projetos: v.map((x) => Number(x.id)) })}
            renderTags={(tagVal, getTagProps) =>
              tagVal.map((opt, i) => (
                <Chip variant="outlined" label={opt.nome || opt.id} size="small" {...getTagProps({ index: i })} key={opt.id} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Empresas (projetos)"
                placeholder="Todas se vazio"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCompanies ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Autocomplete
            multiple
            loading={loadingPositions}
            options={positions}
            getOptionLabel={(o) => (o && o.nome ? String(o.nome) : o?.id != null ? `Cargo #${o.id}` : "")}
            isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
            value={positions.filter((p) => vis.ids_cargos.includes(Number(p.id)))}
            onChange={(_, v) => patchVis({ ids_cargos: v.map((x) => Number(x.id)) })}
            renderTags={(tagVal, getTagProps) =>
              tagVal.map((opt, i) => (
                <Chip variant="outlined" label={opt.nome || opt.id} size="small" {...getTagProps({ index: i })} key={opt.id} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Cargos"
                placeholder="Todos se vazio"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingPositions ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Autocomplete
            multiple
            loading={loadingUsers}
            options={users}
            getOptionLabel={(o) => {
              if (!o) return "";
              const n = o.nome || "";
              const e = o.email || "";
              return e ? `${n} (${e})` : n || `Usuário #${o.id}`;
            }}
            isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
            value={users.filter((u) => vis.ids_usuarios.includes(Number(u.id)))}
            onChange={(_, v) => patchVis({ ids_usuarios: v.map((x) => Number(x.id)) })}
            renderTags={(tagVal, getTagProps) =>
              tagVal.map((opt, i) => (
                <Chip
                  variant="outlined"
                  label={opt.nome || opt.email || opt.id}
                  size="small"
                  {...getTagProps({ index: i })}
                  key={opt.id}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Usuários"
                placeholder="Todos se vazio"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingUsers ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
      </Paper>

      <TextField
        label="Introdução do treinamento (opcional)"
        multiline
        minRows={2}
        fullWidth
        value={data.intro}
        onChange={(e) => setIntro(e.target.value)}
      />

      {data.modules.map((mod, mi) => (
        <Paper key={mod.id || mi} variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "flex-start" }}>
            <TextField
              label="Título do módulo"
              fullWidth
              value={mod.title}
              onChange={(e) => patchModule(mi, { title: e.target.value })}
            />
            <TextField
              label="Ordem"
              type="number"
              sx={{ width: 100 }}
              value={mod.order ?? mi}
              onChange={(e) => patchModule(mi, { order: Math.trunc(Number(e.target.value)) || 0 })}
            />
            <IconButton color="error" onClick={() => removeModule(mi)} aria-label="Remover módulo">
              <DeleteOutlineRoundedIcon />
            </IconButton>
          </Box>

          {(mod.items || []).map((it, ii) => (
            <Box
              key={it.id || ii}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "120px 1fr 1fr" },
                gap: 1,
                mb: 1.5,
                alignItems: "start",
              }}
            >
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select
                  label="Tipo"
                  value={it.kind || "pdf"}
                  onChange={(e) => patchItem(mi, ii, { kind: e.target.value })}
                >
                  {KINDS.map((k) => (
                    <MenuItem key={k.value} value={k.value}>
                      {k.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Título do material"
                size="small"
                fullWidth
                value={it.title}
                onChange={(e) => patchItem(mi, ii, { title: e.target.value })}
              />
              <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                <TextField
                  label="URL"
                  size="small"
                  fullWidth
                  value={it.url}
                  onChange={(e) => patchItem(mi, ii, { url: e.target.value })}
                  placeholder="https://..."
                />
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  startIcon={<CloudUploadIcon />}
                  disabled={uploading}
                  sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  Enviar
                  <input
                    type="file"
                    hidden
                    accept="application/pdf,video/*,image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (f) handleFilePick(mi, ii, f);
                    }}
                  />
                </Button>
                <IconButton size="small" color="error" onClick={() => removeItem(mi, ii)} aria-label="Remover item">
                  <DeleteOutlineRoundedIcon />
                </IconButton>
              </Box>
              {uploading && uploadKey === `${mi}-${ii}` && <LinearProgress sx={{ gridColumn: "1 / -1" }} />}
              <TextField
                label="Notas para IA / resumo (recomendado para vídeo e imagem)"
                size="small"
                fullWidth
                multiline
                minRows={2}
                sx={{ gridColumn: "1 / -1" }}
                value={it.notes}
                onChange={(e) => patchItem(mi, ii, { notes: e.target.value })}
              />
            </Box>
          ))}

          <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => addItem(mi)}>
            Adicionar material ao módulo
          </Button>
        </Paper>
      ))}

      <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={addModule}>
        Adicionar módulo
      </Button>
    </Box>
  );
}
